import React, { useState, useEffect } from "react";
import { useApp } from "../context/AppContext";
import api from "../api/api";
import Attachments from "../UserControls/Attachments/Attachments";
import { Briefcase, Home as HomeIcon, MoreHorizontal } from "lucide-react";

const getShortNameFormValues = (shortName = "") => {
  const cleanName = String(shortName || "").trim();
  if (cleanName === "Home" || cleanName === "Office") {
    return { shortNameType: cleanName, shortNameOther: "" };
  }
  return { shortNameType: cleanName ? "Other" : "Home", shortNameOther: cleanName };
};

export default function Profile({ onVerify, onClose, customer }) {
  const { user, logout, setUser } = useApp();
  const [form, setForm] = useState({});
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [isAttachmentsOpen, setIsAttachmentsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    const currentUser = user || JSON.parse(localStorage.getItem("user") || "{}");
    if (!currentUser?.Vid) return;

    const userType = currentUser.UserType || "Existing User";

    const fetchProfile = async () => {
      try {
        setLoading(true);
        const res = await api.get(
          `GetIndividualCustomerList?Vid=${currentUser.Vid}&UserType=${encodeURIComponent(userType)}`
        );

        const customer =
          res?.data?.GetIndividualCustomerListjs?.Table?.[0] ||
          res?.GetIndividualCustomerListjs?.Table?.[0] ||
          null;
          
        const companyData =
          res?.data?.GetIndividualCustomerListjs?.Table1?.[0] ||
          res?.GetIndividualCustomerListjs?.Table1?.[0] ||
          null;

        if (customer) {
          const updatedUser = {
            Vid: customer.Vid ?? "",
            Vcode: customer.VCode ?? "",
            name: customer.Vname?.trim() || "",
            mail: customer.Mail?.trim() || "",
            address: customer.Address1?.trim() || "",
            landmark: customer.Address2?.trim() || "", 
            area: customer.City?.trim() || "",
            division: customer.Division?.trim() || "",
            pin: (customer.Pincode || "").trim(),
            ShortName: customer.ShortName?.trim() || "",
            ...getShortNameFormValues(customer.ShortName),
            UserType: userType,
            IsCodAvail: customer.IsCodAvail ?? currentUser.IsCodAvail ?? 0,
             PinAllowed: customer.PinAllowed,
            companyAddress: companyData?.ComAddress?.trim() || "No.5/25, Balaji Street,Arumbakkam,Opp. Police Quarters, Taluk Office Road,Sri Venkatesa Perumal Nagar, Chennai -600106.",
            companyName: companyData?.ComName?.trim() || "Hitham Associates Private Limited",
            companyEmail: companyData?.Email?.trim() || "annamatchennai@gmail.com",
            companyState: companyData?.State?.trim() || "TAMILNADU"
          };

          setForm(updatedUser);
          setUser(updatedUser);
          localStorage.setItem("user", JSON.stringify(updatedUser));
        }
      } catch (err) {
        console.error("Profile fetch failed:", err);
        setMessage("Failed to load profile. Try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!form.Vid) return;

    // Validate name is required
    if (!form.name || form.name.trim() === '') {
      setMessage('Name is required!');
      setTimeout(() => setMessage(''), 2000);
      return;
    }

    // Validate pincode is exactly 6 digits
    if (!form.pin || !/^\d{6}$/.test(form.pin)) {
      setMessage('Pincode must be exactly 6 digits!');
      setTimeout(() => setMessage(''), 2000);
      return;
    }

    const shortName = form.shortNameType === "Other" ? String(form.shortNameOther || "").trim() : form.shortNameType || "Home";
    if (!shortName) {
      setMessage('Please enter address type!');
      setTimeout(() => setMessage(''), 2000);
      return;
    }

    setLoading(true);
    setIsAttachmentsOpen(false); // Close modal before API call

    const payload = {
      Items: [
        {
          Vid: form.Vid,
          Vcode: form.Vcode || "NA",
          Vname: form.name || "NA",
          Mail: form.mail || " ",
          Address1: form.address || "NA",
          Address2: form.landmark || "NA",
          City: form.area || "NA",
          division: form.division || "NA",
          Pincode: form.pin || "000000",
          ShortName: shortName,
        },
      ],
      UserType: form.UserType || "Existing User",
    };

    console.log("📦 Payload to UpdateProfile:", JSON.stringify(payload, null, 2));

    try {
      const response = await api.UpdateProfile(payload);
      console.log("✅ UpdateProfile Response:", response.data);

      // Call GetIndividualCustomerList after UpdateProfile
      const userType = form.UserType || "Existing User";
      const customerResponse = await api.GetIndividualCustomerList(form.Vid, userType);
      console.log("✅ GetIndividualCustomerList Response:", customerResponse);

      const customer =
        customerResponse?.data?.GetIndividualCustomerListjs?.Table?.[0] ||
        customerResponse?.GetIndividualCustomerListjs?.Table?.[0] ||
        null;
        
      const companyData =
        customerResponse?.data?.GetIndividualCustomerListjs?.Table1?.[0] ||
        customerResponse?.GetIndividualCustomerListjs?.Table1?.[0] ||
        null;

      if (customer) {
        const updatedUser = {
          Vid: customer.Vid ?? "",
          Vcode: customer.VCode ?? "",
          name: customer.Vname?.trim() || "",
          mail: customer.Mail?.trim() || "",
          address: customer.Address1?.trim() || "",
          landmark: customer.Address2?.trim() || "", 
          area: customer.City?.trim() || "",
          division: customer.Division?.trim() || "",
          pin: (customer.Pincode || "").trim(),
          ShortName: customer.ShortName?.trim() || shortName,
          ...getShortNameFormValues(customer.ShortName || shortName),
          UserType: userType,
          IsCodAvail: customer.IsCodAvail ?? form.IsCodAvail ?? 0,
          PinAllowed: customer.PinAllowed,
          companyAddress: companyData?.ComAddress?.trim() || "No.5/25, Balaji Street,Arumbakkam,Opp. Police Quarters, Taluk Office Road,Sri Venkatesa Perumal Nagar, Chennai -600106.",
          companyName: companyData?.ComName?.trim() || "Hitham Associates Private Limited",
          companyEmail: companyData?.Email?.trim() || "annamatchennai@gmail.com",
          companyState: companyData?.State?.trim() || "TAMILNADU"
        };

        setForm(updatedUser);
        setUser(updatedUser);
        localStorage.setItem("user", JSON.stringify(updatedUser));
      }

      setMessage("Profile updated successfully!");
      setIsEditing(false);   
      
      if (onVerify) onVerify(form.Vid, true);
      if (onClose) onClose();
    } catch (error) {
      console.error("❌ Error updating profile:", error);
      alert("Failed to update profile. Please check console for details.");
    } finally {
      setLoading(false);
      setTimeout(() => setMessage(""), 2000);
    }
  };

  return (
    <div className="w-full max-w-md p-6 mx-auto bg-white shadow-lg animate-fade-in md:max-w-2xl md:p-8 rounded-2xl">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-red-600">
          My Profile
        </h2>
        <button
          type="button"
          onClick={() => setIsEditing(!isEditing)}
          className="p-2 transition-colors rounded-full hover:bg-gray-100"
        >
          {isEditing ? '🔒' : '✏️'}
        </button>
      </div>

      {loading ? (
        <p className="text-center text-gray-500">Loading...</p>
      ) : (
        <form onSubmit={save} className="space-y-3">
          <input
            value={form.name || ""}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            readOnly={!isEditing}
            className={`w-full border p-3 rounded-md ${!isEditing ? 'bg-gray-100' : ''}`}
            placeholder="Name"
          />
          <input
            value={form.mail || ""}
            onChange={(e) => setForm({ ...form, mail: e.target.value })}
            type="email"
            readOnly={!isEditing}
            className={`w-full border p-3 rounded-md ${!isEditing ? 'bg-gray-100' : ''}`}
            placeholder="Email"
          />
          <input
            value={form.Vcode || ""}
            readOnly
            type="tel"
            className="w-full p-3 bg-gray-100 border rounded-md"
            placeholder="Mobile"
          />
          <input
            value={form.area || ""}
            readOnly
            className="w-full p-3 bg-gray-100 border rounded-md"
            placeholder="Area"
          />
          <input
            value={form.division || ""}
            readOnly
            className="w-full p-3 bg-gray-100 border rounded-md"
            placeholder="Division"
          />
          <textarea
            value={form.address || ""}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
            readOnly={!isEditing}
            className={`w-full border p-3 rounded-md ${!isEditing ? 'bg-gray-100' : ''}`}
            placeholder="Address"
          />
          <input
            value={form.landmark || ""}
            onChange={(e) => setForm({ ...form, landmark: e.target.value })}
            readOnly={!isEditing}
            className={`w-full border p-3 rounded-md ${!isEditing ? 'bg-gray-100' : ''}`}
            placeholder="Landmark"
          />
          <input
            value={form.pin || ""}
            onChange={(e) => setForm({ ...form, pin: e.target.value })}
            readOnly={!isEditing}
            maxLength="6"
            className={`w-full border p-3 rounded-md ${!isEditing ? 'bg-gray-100' : ''}`}
            placeholder="Pincode"
          />
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2">
              {[
                { label: "Home", icon: HomeIcon },
                { label: "Office", icon: Briefcase },
                { label: "Other", icon: MoreHorizontal },
              ].map(({ label, icon: Icon }) => (
                <button
                  key={label}
                  type="button"
                  disabled={!isEditing}
                  onClick={() => setForm({ ...form, shortNameType: label })}
                  className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold border rounded-full transition-colors ${
                    form.shortNameType === label
                      ? "text-white bg-red-500 border-red-500"
                      : "text-gray-700 bg-white border-gray-200 hover:bg-red-50 hover:border-red-200"
                  } ${!isEditing ? "cursor-default opacity-90" : ""}`}
                >
                  <Icon size={13} />
                  {label}
                </button>
              ))}
            </div>
            {form.shortNameType === "Other" && (
              <input
                value={form.shortNameOther || ""}
                onChange={(e) => setForm({ ...form, shortNameOther: e.target.value })}
                readOnly={!isEditing}
                placeholder="Enter address type"
                className={`w-full p-3 border rounded-md ${!isEditing ? "bg-gray-100" : ""}`}
                required={isEditing}
              />
            )}
          </div>
         {/* <div className="p-4 mt-3 border border-gray-200 rounded-lg bg-gray-50">
  <label className="block mb-2 text-sm font-semibold text-red-600">
    Location Attachments
  </label> */}
  {/* {form.UserType !== "New User" && (
    <div className="p-4 mt-3 border border-gray-200 rounded-lg bg-gray-50">
      <label className="block mb-2 text-sm font-semibold text-red-600">
        Location Attachments
      </label>
      <Attachments
        mdlId="LocationAttachments"
        primeId={form.Vid}
        primeName={form.name}
        uploadedBy={localStorage.getItem("user")}
        isOpen={isAttachmentsOpen}
        onOpenChange={setIsAttachmentsOpen}
      />
    </div>
  )} */}

          {isEditing && (
            <button
              type="submit"
              className="w-full py-3 font-bold text-white bg-red-500 rounded-lg hover:bg-red-600"
            >
              Save
            </button>
          )}
        </form>
      )}

      {message && <p className="mt-3 text-center text-green-600">{message}</p>}

      <button
        onClick={logout}
        className="w-full py-3 mt-6 font-semibold text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
      >
        Logout
      </button>
    </div>
  );
}
